<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('stock_management.orders.title')"
      :grid-ref="$refs[gridRefKey]"
      :show-add="canEdit"
      :show-filter="false"
      @add="handleAddClick"
      :show-refresh="true"
    />
    <div class="color-labels" style="margin-bottom: 16px; display: flex; gap: 16px; align-items: center">
      <span style="display: flex; align-items: center"> {{ $t("common.remaining_percentage") }}: </span>
      <span style="display: flex; align-items: center">
        <span
          style="
            display: inline-block;
            width: 12px;
            height: 12px;
            background: #1a8f3d;
            border-radius: 3px;
            margin-right: 6px;
            border: 1px solid #ccc;
          "
        ></span>
        <span style="color: #1a8f3d; font-weight: 500">0%</span>
      </span>
      <span style="display: flex; align-items: center">
        <span
          style="
            display: inline-block;
            width: 12px;
            height: 12px;
            background: #ea652a;
            border-radius: 3px;
            margin-right: 6px;
            border: 1px solid #ccc;
          "
        ></span>
        <span style="color: #ea652a; font-weight: 500">1-10%</span>
      </span>
      <span style="display: flex; align-items: center">
        <span
          style="
            display: inline-block;
            width: 12px;
            height: 12px;
            background: #f0ad4e;
            border-radius: 3px;
            margin-right: 6px;
            border: 1px solid #ccc;
          "
        ></span>
        <span style="color: #f0ad4e; font-weight: 500">11-50%</span>
      </span>
      <span style="display: flex; align-items: center">
        <span
          style="
            display: inline-block;
            width: 12px;
            height: 12px;
            background: #fff;
            border-radius: 3px;
            margin-right: 6px;
            border: 1px solid #ccc;
          "
        ></span>
        <span style="color: #333; font-weight: 500">51-100%</span>
      </span>
      <span style="display: flex; align-items: center">
        <span
          style="
            display: inline-block;
            width: 12px;
            height: 12px;
            background: #dc3545;
            border-radius: 3px;
            margin-right: 6px;
            border: 1px solid #ccc;
          "
        ></span>
        <span style="color: #dc3545; font-weight: 500">&lt; 0%</span>
      </span>
    </div>
    <DxDataGrid
      :ref="gridRefKey"
      :data-source="ordersDataSource"
      :remote-operations="gridRemoteOperations"
      :column-min-width="80"
      style="min-height: 350px"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :load-panel="{ enabled: true }"
      :element-attr="gridAttributes"
      @exporting="onExporting"
      @editing-start="onEditingStart"
      @option-changed="onOptionChanged"
      @content-ready="onContentReady"
      @row-prepared="onRowPrepared"
      :row-height="50"
    >
      <DxColumn
        :allowHeaderFiltering="false"
        :allowFiltering="false"
        :allowSorting="false"
        :fixed="true"
        fixed-position="left"
        :showInColumnChooser="false"
        :allowHiding="false"
        :width="50"
        :min-width="50"
        :max-width="50"
        cell-template="deliveryDateWarningTemplate"
      />
      <template #deliveryDateWarningTemplate="{ data }">
        <div>
          <span v-if="isDeliveryDateExceeded(data.data)">
            <i
              class="dx-icon-warning"
              style="color: red; font-size: 19px"
              :title="$t('stock_management.contracts_purchase_sale.delivery_date_exceeded')"
            ></i>
          </span>
          <span v-if="isTodayAndDeliveryDateDifferenceLessThan(7, data.data.delivery_date) && !isDeliveryDateExceeded(data.data)">
            <i class="dx-icon-warning" style="color: orange; font-size: 19px" :title="$t('common.delivery_date_is_approaching')"></i>
          </span>
        </div>
      </template>
      <DxColumn
        dataField="legal_entity_name"
        :caption="$t('common.legal_entity_name')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :width="150"
      ></DxColumn>
      <DxColumn
        dataField="partner_name"
        :caption="$t('common.partner')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :width="150"
      ></DxColumn>
      <DxColumn
        dataField="order_number"
        :caption="$t('stock_management.orders.order_number')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :width="150"
      ></DxColumn>
      <DxColumn
        dataField="contract_number"
        :caption="$t('stock_management.contracts_purchase_sale.contract_number')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :width="150"
      ></DxColumn>
      <DxColumn
        dataField="order_type_id"
        cell-template="orderTypeViewTemplate"
        :caption="$t('stock_management.orders.order_type')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :width="150"
        :calculateCellValue="getOrderTypeName"
        :allowSorting="false"
      >
        <DxLookup :data-source="orderTypesList" value-expr="order_type_id" display-expr="order_type" />
      </DxColumn>
      <template #orderTypeViewTemplate="{ data }">
        <span>
          {{ data.data.order_type }}
        </span>
      </template>
      <DxColumn
        dataField="date"
        :caption="$t('stock_management.orders.date')"
        :sortOrder="'desc'"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        dataType="date"
        :allowEditing="false"
        :format="getDxGridDefaultDateFormat"
        :width="120"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="delivery_date"
        :caption="$t('common.delivery_date')"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        dataType="date"
        :format="getDxGridDefaultDateFormat"
        :width="120"
        :allowEditing="false"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="product_name"
        :caption="$t('common.product')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="ordered_quantity"
        :caption="$t('stock_management.orders.ordered_quantity')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="delivered_quantity"
        :caption="$t('common.delivered_quantity')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="receptioned_quantity"
        :caption="$t('stock_management.orders.receptioned_quantity')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="remaining_percentage"
        :caption="$t('common.remaining_percentage')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="false"
      ></DxColumn>
      <DxColumn
        dataField="remaining_quantity"
        :caption="$t('common.remaining_quantity')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="false"
      ></DxColumn>
      <DxColumn
        dataField="measure_unit"
        :caption="$t('common.measure_unit')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="notes"
        :caption="$t('common.notes')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :width="150"
      >
      </DxColumn>
      <DxColumn
        dataField="created_by"
        :visible="false"
        :caption="$t('common.created_by')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowFiltering="true"
        cell-template="createdByViewTemplate"
        :calculateCellValue="getCreatedByColumnText"
        :width="150"
      >
        <DxLookup :data-source="users" value-expr="uuid" display-expr="name" />
        <DxHeaderFilter
          :allowSelectAll="true"
          :search="{
            enabled: true,
            placeholder: $t('common.search'),
          }"
        />
      </DxColumn>
      <template #createdByViewTemplate="{ data }">
        <span>
          {{ getCreatedByColumnText(data.data.created_by) }}
        </span>
      </template>
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs[gridRefKey]"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeletingRow"
        />
      </template>
      <DxEditing mode="row" :allowUpdating="false" :allowAdding="false" :allowDeleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('stock_management.orders.title'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxHeaderFilter :visible="true" />
      <DxScrolling show-scrollbar="always" :use-native="false" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxSearchPanel :visible="false" :width="240" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'SmOrders'" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxSummary>
        <DxTotalItem column="ordered_quantity" summaryType="sum" valueFormat="#,##0.##" displayFormat="Total: {0}" />
        <DxTotalItem column="delivered_quantity" summaryType="sum" valueFormat="#,##0.##" displayFormat="Total: {0}" />
        <DxTotalItem column="remaining_quantity" summaryType="sum" valueFormat="#,##0.##" displayFormat="Total: {0}" />

        <DxGroupItem column="ordered_quantity" summaryType="sum" valueFormat="#,##0.##" displayFormat="Group Total: {0}" />
        <DxGroupItem column="delivered_quantity" summaryType="sum" valueFormat="#,##0.##" displayFormat="Group Total: {0}" />
        <DxGroupItem column="remaining_quantity" summaryType="sum" valueFormat="#,##0.##" displayFormat="Group Total: {0}" />
      </DxSummary>

      <DxMasterDetail :enabled="true" template="detailTemplate" />
      <template #detailTemplate="{ data }">
        <OrdersDetailList :order="data.data"> </OrdersDetailList>
      </template>
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxScrolling,
  DxTexts,
  DxButton,
  DxPopup,
  DxToolbar,
  DxItem,
  DxSearchPanel,
  DxExport,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxLookup,
  DxSummary,
  DxTotalItem,
  DxGroupItem,
  DxMasterDetail,
} from "devextreme-vue/data-grid";
import Forbidden from "~/pages/errors/Forbidden.vue";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import notify from "devextreme/ui/notify";
import { errorMessage } from "@/helpers/common";
import { saveDxGridColumnSettings, loadDxGridColumnSettings } from "@/helpers/dxGridHelper";
import OrdersDetailList from "~/pages/stock-management/orders/partials/OrdersDetailList.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";

export default {
  name: "OrdersList",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxButton,
    DxPaging,
    DxPager,
    DxPopup,
    DxToolbar,
    DxItem,
    Forbidden,
    DxSearchPanel,
    DxExport,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxLookup,
    DxSummary,
    DxTotalItem,
    DxGroupItem,
    DxMasterDetail,
    OrdersDetailList,
    DxScrolling,
    GridPanelHeader,
    DxActionMenu,
  },
  data() {
    return {
      orderTypesList: [],
      gridAttributes: {
        class: "smagria-grid orders-grid",
      },
      gridRefKey: "ordersGrid",
      ordersDataSource: new DataSource({
        store: new CustomStore({
          key: "uuid",
          load: async (loadOptions) => {
            let dataSource = {
              data: [],
              totalCount: 0,
            };

            if (this.visibilitySettingsLoading || !this.visibilitySettingsLoaded) {
              return dataSource;
            }

            let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
            let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

            let payload = {
              page: pageNumber,
              limit: pageLimit,
              filter: loadOptions.filter,
              sort: loadOptions.sort,
            };

            try {
              await this.getSmOrders(payload).then((response) => {
                dataSource.data = response.data.data;
                dataSource.totalCount = response.data?.pagination?.total ?? 1;

                if (response.data?.summary && Array.isArray(response.data.summary)) {
                  dataSource.summary = response.data.summary;
                } else if (Array.isArray(dataSource.data)) {
                  const totals = [0, 0, 0];
                  dataSource.data.forEach((row) => {
                    totals[0] += Number(row?.ordered_quantity) || 0;
                    totals[1] += Number(row?.delivered_quantity) || 0;
                    totals[2] += Number(row?.remaining_quantity) || 0;
                  });
                  dataSource.summary = totals;
                }
              });

              return dataSource;
            } catch (error) {
              console.error("Error fetching orders:", error);
              throw error;
            }
          },
          remove: this.removeSmOrder,
        }),
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
        summary: true, // if not true tha pagination will not work properly
      },
      pageSizes: [15, 25, 100],
      editingRowData: null,
      yes_no_list: [
        {
          id: 1,
          label: this.$t("common.yes"),
        },
        {
          id: 0,
          label: this.$t("common.no"),
        },
      ],
      visibilitySettingsLoaded: false,
      visibilitySettingsLoading: false,
      saveColumnSettingsTimeout: null,
    };
  },
  computed: {
    ...mapGetters({
      users: "user/users",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.SM_ORDERS,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    dataGridInstance: function () {
      const grid = this.$refs && this.$refs[this.gridRefKey] ? this.$refs[this.gridRefKey] : null;
      return grid && grid.instance ? grid.instance : null;
    },
  },
  methods: {
    ...mapActions({
      getSmOrders: "smOrders/getSmOrders",
      deleteSmOrder: "smOrders/deleteSmOrder",
      getDxGridSettings: "dxgrid/getDxGridSettings",
      setDxGridSettings: "dxgrid/setDxGridSettings",
      getUsers: "user/getUsers",
      getSmOrderTypes: "smOrders/getSmOrderTypes",
    }),
    handleAddClick(e) {
      this.editingRowData = null;
      this.$router.push({
        name: "orders-edit",
        params: {
          uuid: "add",
        },
      });
    },
    allowUpdating(component) {
      return this.canEdit && !component.row.data.deleted_at;
    },
    allowDeleting(component) {
      return this.canDelete && !component.row.data.deleted_at;
    },
    allowDeletingRow(row) {
      return this.canDelete && row && !row.deleted_at;
    },
    onRowPrepared(e) {
      if (e.rowType === "data") {
        const percent = e.data.remaining_percentage;
        if (typeof percent === "number") {
          if (percent === 0) {
            e.rowElement.classList.add("smagria-finished-row");
          } else if (percent >= 1 && percent <= 10) {
            e.rowElement.classList.add("smagria-warning-row");
          } else if (percent >= 11 && percent <= 50) {
            e.rowElement.classList.add("smagria-unfinished-row");
          } else if (percent >= 51 && percent <= 100) {
            e.rowElement.style.backgroundColor = "#fff";
            e.rowElement.style.color = "#000";
          } else if (percent < 0) {
            e.rowElement.classList.add("smagria-danger-row");
          }
        }
      }
    },
    onEditingStart(e) {
      e.cancel = true;
      this.editingRowData = e.data;

      if (e.data.deleted_at) {
        errorMessage(this, this.$t("stock_management.orders.error_edit_deleted"));
        return;
      }

      this.$router.push({
        name: "orders-edit",
        params: {
          uuid: e.data.header_uuid,
        },
      });
    },
    async removeSmOrder(key) {
      try {
        await this.deleteSmOrder({ uuid: key }).then((response) => {
          this.notifyMsg(response);
        });
      } catch (error) {
        console.error("Error deleting orders:", error);
        throw error;
      }
    },
    onExporting(e) {
      e.fileName = "SmOrders_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        const fields = ["deleted_at"];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SmOrders");
        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (gridCell?.column?.dataField === "order_type_id" && excelCell.row > 1) {
              excelCell.value = gridCell.value;
            } else if (gridCell?.column?.dataField == "created_by" && excelCell.row > 1 && gridCell.data) {
              excelCell.value = this.getCreatedByColumnText(gridCell.data[gridCell?.column?.dataField]);
            } else if (fields.includes(gridCell?.column?.dataField) && excelCell.row > 1 && gridCell.data) {
              excelCell.value = this.getYesNoColumnText(!!gridCell.data[gridCell?.column?.dataField]);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    getOrderTypeName(rowData) {
      const type = this.orderTypesList.find((item) => item.order_type_id === rowData.order_type_id);
      return type ? type.order_type : null;
    },
    notifyMsg(response) {
      notify({
        message: response.message ?? "",
        type: response.status == "ok" ? "success" : "error",
        displayTime: 2500,
        width: "auto",
        position: { at: "top center", my: "top center" },
      });
    },
    getYesNoColumnText(value) {
      if (value === null) {
        return "";
      }
      return this.yes_no_list.find((item) => item.id == value)?.label;
    },
    getCreatedByColumnText(value) {
      if (!value) {
        return "";
      }
      const users = Array.isArray(this.users) ? this.users : [];
      const user = users.find((user) => user.uuid === value);
      return user ? user.name : this.$t("common.unknown_user");
    },
    onOptionChanged(e) {
      saveDxGridColumnSettings(this, e);
    },
    onContentReady(e) {
      loadDxGridColumnSettings(this);
    },
    isTodayAndDeliveryDateDifferenceLessThan(days, deliveryDate) {
      if (!deliveryDate) return false;

      const today = new Date();
      const delivery = new Date(deliveryDate);
      const diffTime = Math.abs(delivery - today);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays < days;
    },
    isDeliveryDateExceeded(data) {
      const deliveryDate = data.delivery_date;
      if (!deliveryDate) return false;

      const today = new Date();
      const delivery = new Date(deliveryDate);

      return delivery < today && data.remaining_quantity > 0;
    },
  },
  mounted() {
    this.getSmOrderTypes().then((response) => {
      if (response && response.length > 0) {
        this.orderTypesList = Object.keys(response).map((key) => {
          return {
            order_type_id: response[key].id,
            order_type: response[key].name,
          };
        });
      }
    });

    this.getUsers({
      enablePagination: 0,
      limit: 1000,
    });
  },
};
</script>

<style scoped>
.orders-grid {
  height: calc(95vh - 170px);
}

.color-labels {
  margin-bottom: 16px;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
}
</style>
